
import { useState, useEffect, useMemo, useCallback } from "react";
import { Member } from "@/types/member";
import { Settlement } from "@/types/payment";
import { PaymentMethod } from "@/types/payment";
import { SettlementItem } from "./SettlementItem";
import { PaymentMethodDialog } from "./PaymentMethodDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { safeInsert } from "@/utils/supabaseHelpers";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface SettlementsProps {
  settlements: Settlement[];
  members: Member[];
  currencies: { code: string; symbol: string }[];
  currentUserId: string | undefined;
  groupId: string;
  onSettlementUpdate: () => void;
  onRefresh?: () => Promise<void>;
}

export const Settlements = ({ 
  settlements, 
  members,
  currencies,
  currentUserId,
  groupId,
  onSettlementUpdate,
  onRefresh
}: SettlementsProps) => {
  const { t } = useTranslation();
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement | null>(null);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [receiverPaymentMethods, setReceiverPaymentMethods] = useState<PaymentMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    if (settlements.length > 0) {
      setInitialLoadComplete(true);
    }
  }, [settlements]);
  
  const sortedSettlements = useMemo(() => {
    return [...settlements].sort((a, b) => {
      if ((a.isPaid || false) !== (b.isPaid || false)) {
        return (a.isPaid || false) ? 1 : -1;
      }
      
      const aIsUser = currentUserId === a.from;
      const bIsUser = currentUserId === b.from;
      return Number(bIsUser) - Number(aIsUser);
    });
  }, [settlements, currentUserId]);

  const fetchReceiverPaymentMethods = useCallback(async (userId: string) => {
    try {
      console.log("Fetching payment methods for user:", userId);
      
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('profile_id', userId as string)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching payment methods:', error);
        return null;
      }
      
      if (data) {
        console.log("Payment method data retrieved:", data);
        
        // Map database fields to our PaymentMethod type
        const mappedData: PaymentMethod = {
          id: data.id,
          profile_id: data.profile_id,
          // Use the paypay_qr_url as the username if it doesn't look like a storage URL
          paypay_username: data.paypay_qr_url && !data.paypay_qr_url.includes('supabase.co/storage') 
            ? data.paypay_qr_url 
            : null,
          paypay_qr_url: data.paypay_qr_url,
          bank_name: data.bank_name,
          branch_name: data.branch_name,
          branch_code: data.branch_code,
          bank_code: data.bank_code,
          account_type: data.account_type,
          account_number: data.account_number,
          account_holder_name: data.account_holder_name,
          is_preferred: data.is_preferred || false
        };
        
        console.log("Mapped payment method data:", mappedData);
        return mappedData;
      }
      
      return null;
    } catch (error) {
      console.error('Exception fetching payment methods:', error);
      return null;
    }
  }, []);

  const handleSettlePayment = useCallback(async (settlement: Settlement) => {
    try {
      const methods = await fetchReceiverPaymentMethods(settlement.to);
      setReceiverPaymentMethods(methods);
      setSelectedSettlement(settlement);
      setShowPaymentMethodDialog(true);
    } catch (error) {
      console.error('Error in handleSettlePayment:', error);
    }
  }, [fetchReceiverPaymentMethods]);

  const handleConfirmPayment = useCallback(async (settlement: Settlement) => {
    if (!settlement) return;
    
    try {
      const roundedAmount = Math.round(settlement.amount);
      
      const result = await safeInsert(
        'settlements', 
        {
          group_id: groupId,
          from_user: settlement.from,
          to_user: settlement.to,
          amount: roundedAmount,
          currency: settlement.currency,
          is_paid: true,
          paid_at: new Date().toISOString()
        } as any
      );

      if (!result.success) {
        console.error('Error confirming payment:', result.error);
        toast({
          title: t('common.error'),
          description: t('settlements.confirmError', 'Failed to confirm payment. Please try again.'),
          variant: "destructive",
        });
        return;
      }

      setShowPaymentMethodDialog(false);
      onSettlementUpdate();
      
      toast({
        description: t('settlements.paymentConfirmed', 'Payment marked as complete.'),
        variant: "default",
      });

    } catch (error) {
      console.error("Error confirming payment:", error);
      toast({
        title: t('common.error'),
        description: t('settlements.confirmError', 'Failed to confirm payment. Please try again.'),
        variant: "destructive",
      });
    }
  }, [groupId, onSettlementUpdate, toast, t]);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    
    setIsLoading(true);
    try {
      await onRefresh();
    } catch (error) {
      console.error("Error refreshing settlements:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('settlements.title')}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {settlements.filter(s => !s.isPaid).length} {t('settlements.pending')} / {settlements.length} {t('settlements.total')}
          </span>
          {onRefresh && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isLoading}
              className="h-8 w-8 p-0"
              aria-label={t('common.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {!initialLoadComplete ? (
          <div className="p-4 rounded-lg bg-white/50 dark:bg-black/50 text-center">
            <div className="flex flex-col items-center justify-center space-y-2">
              <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          </div>
        ) : sortedSettlements.length > 0 ? (
          sortedSettlements.map((settlement, index) => (
            <SettlementItem
              key={`${settlement.from}-${settlement.to}-${index}`}
              settlement={settlement}
              members={members}
              currencies={currencies}
              isUserPayment={settlement.from === currentUserId}
              onSettlePayment={settlement.from === currentUserId && !(settlement.isPaid || false) ? () => handleSettlePayment(settlement) : undefined}
            />
          ))
        ) : (
          <div className="p-4 rounded-lg bg-white/50 dark:bg-black/50 text-center">
            <p className="text-sm text-muted-foreground">
              {t('groups.allExpensesBalanced')}
            </p>
          </div>
        )}
      </div>

      <PaymentMethodDialog
        open={showPaymentMethodDialog}
        onOpenChange={setShowPaymentMethodDialog}
        selectedSettlement={selectedSettlement}
        paymentMethod={receiverPaymentMethods}
        onConfirmPayment={handleConfirmPayment}
      />
    </div>
  );
};
